/**
 * The overlay stack — how set elements (logo, title, subheader, body, category
 * pill) are laid out, shared by the card, the template editor and both
 * Remotion compositions; `tend_service/services/overlay_renderer.py` carries
 * the same arithmetic for the image render. Keep them in lockstep.
 *
 * Model (Safe Zones punch list 5–6 + "Research Main Overlay Cleanup", 2026-10-07):
 * - ONE column on the channel's safe rectangle: the top stack flows down from
 *   the top safe edge, the bottom stack up from the bottom safe edge, the
 *   middle stack centres in whatever space is left. Nothing can overlap.
 * - Inside a stack the order is fixed (top/middle: logo, title, subheader,
 *   body, category; bottom: category, title, subheader, body, logo).
 * - Every element has a GAP below it to the next element (or the safe edge):
 *   the VISIBLE distance between boxes (glyph box, pill, logo), reference px
 *   at width 360, floor 0. The 4 px padding a text box carries for its
 *   background/border never counts. Absent = STACK_DEFAULT_GAP (8) for every
 *   pair — one number, decided 2026-10-07; the renderers subtract their own
 *   padding to get there.
 * - No translate anywhere (the old `{kind}VerticalOffset` is gone).
 */
import type { CSSProperties } from 'react';
import { OVERLAY_CONSTANTS } from '../hooks/useOverlayScale';
import type { SafeZoneInsets } from './safeZones';

export type SetElementKind = 'logo' | 'title' | 'subheader' | 'body' | 'category';
export type OverlayRow = 'top' | 'middle' | 'bottom';
export const SET_ELEMENT_KINDS: SetElementKind[] = ['logo', 'title', 'subheader', 'body', 'category'];
export const OVERLAY_ROWS: OverlayRow[] = ['top', 'middle', 'bottom'];

export const STACK_ORDER: Record<OverlayRow, SetElementKind[]> = {
  top: ['logo', 'title', 'subheader', 'body', 'category'],
  middle: ['logo', 'title', 'subheader', 'body', 'category'],
  bottom: ['category', 'title', 'subheader', 'body', 'logo'],
};

/** The default visible gap below any element when none is stored (reference px). */
export const STACK_DEFAULT_GAP = 8;
/** Reference px of padding on a text box; never part of a gap. */
export const STACK_TEXT_PAD = OVERLAY_CONSTANTS.TEXT_PADDING;
/** Arrow step for the gap control, reference px. */
export const GAP_STEP = 2;

export const isTextKind = (kind: SetElementKind | null | undefined): boolean =>
  kind === 'title' || kind === 'subheader' || kind === 'body';

export function rowOf(position: string | null | undefined): OverlayRow {
  const p = position || '';
  if (p.startsWith('top')) return 'top';
  if (p.startsWith('middle')) return 'middle';
  return 'bottom';
}

export type StackGaps = Partial<Record<SetElementKind, number | null | undefined>>;

export interface StackInput {
  positions: Record<SetElementKind, string>;
  visible: Record<SetElementKind, boolean>;
}

/** The visible kinds of one row, in stack order (top → bottom). */
export function stackKindsForRow(row: OverlayRow, input: StackInput): SetElementKind[] {
  return STACK_ORDER[row].filter((kind) => input.visible[kind] && rowOf(input.positions[kind]) === row);
}

/** The visible distance below `kind` when nothing is stored: 8, whatever follows. */
export function defaultGap(_kind: SetElementKind, _next: SetElementKind | null): number {
  return STACK_DEFAULT_GAP;
}

/** CSS margin-bottom (on-screen px) that yields the visible `gap` (reference px, floor 0). */
export function gapToMargin(kind: SetElementKind, next: SetElementKind | null, gap: number | null | undefined, scale: number): number {
  const g = gap == null || !Number.isFinite(gap) ? defaultGap(kind, next) : Math.max(0, gap);
  const m = g - (isTextKind(kind) ? STACK_TEXT_PAD : 0) - (isTextKind(next) ? STACK_TEXT_PAD : 0);
  return Math.round(m * scale);
}

/** margin-bottom (on-screen px) for each element of a row, in order. */
export function stackMargins(kinds: SetElementKind[], gaps: StackGaps, scale: number): number[] {
  return kinds.map((kind, i) => gapToMargin(kind, kinds[i + 1] ?? null, gaps[kind], scale));
}

/** The gap the toolbar shows for `kind`: the stored value or the pair's default. */
export function effectiveGap(kind: SetElementKind, input: StackInput, gaps: StackGaps): number {
  const kinds = stackKindsForRow(rowOf(input.positions[kind]), input);
  const i = kinds.indexOf(kind);
  const next = i >= 0 ? kinds[i + 1] ?? null : null;
  const stored = gaps[kind];
  return stored == null || !Number.isFinite(stored) ? defaultGap(kind, next) : Math.max(0, stored);
}

export const gapKey = (kind: SetElementKind) => `${kind}Gap` as const;

/** Read `{kind}Gap` for every kind out of an overlay / template config. */
export function readGaps(config: Record<string, unknown> | null | undefined): StackGaps {
  const out: StackGaps = {};
  for (const kind of SET_ELEMENT_KINDS) {
    const v = config?.[gapKey(kind)];
    out[kind] = typeof v === 'number' && Number.isFinite(v) ? v : null;
  }
  return out;
}

/**
 * The one column every stack lives in: the safe rectangle, in % of the
 * canvas. Mirrored by overlay.html's `.stack` column.
 */
export function stackColumnStyle(zone: SafeZoneInsets): CSSProperties {
  return {
    position: 'absolute',
    left: `${zone.left}%`,
    right: `${zone.right}%`,
    top: `${zone.top}%`,
    bottom: `${zone.bottom}%`,
    display: 'flex',
    flexDirection: 'column',
  };
}

/**
 * One stack inside the column. Top and bottom take their content height; the
 * middle takes the rest and centres its content. `min-height` stays `auto`
 * on purpose: a stack never shrinks below its content, so when text outgrows
 * the rectangle the column overflows instead of letting stacks overlap.
 */
export function stackBoxStyle(row: OverlayRow): CSSProperties {
  const base: CSSProperties = { display: 'flex', flexDirection: 'column', width: '100%' };
  if (row === 'top') return { ...base, flex: '0 0 auto', justifyContent: 'flex-start' };
  if (row === 'bottom') return { ...base, flex: '0 0 auto', justifyContent: 'flex-end' };
  return { ...base, flex: '1 1 auto', justifyContent: 'center' };
}
